import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { invalidateUserXpRead, readUserXp } from "../utils/userXpReader";
import { subscribeProgressSaved } from "../utils/progressEvents";
import { computeXpBreakdown, ProgressSnapshots, xpProgress } from "../utils/xp";

const features = computeXpBreakdown({}).entries.map(({ feature }) => feature);

export function useUserXp() {
    const { status, user } = useAuth();
    const identity = status === "authenticated" ? user?.identityId : undefined;
    const [attempt, setAttempt] = useState(0);
    useEffect(() => subscribeProgressSaved((change) => {
        if (identity === change.userId && features.some((feature) => feature === change.feature)) {
            setAttempt((value) => value + 1);
        }
    }), [identity]);
    const [state, setState] = useState<{
        identity?: string; status: "loading" | "ready" | "error";
        snapshots: ProgressSnapshots; hasResolved: boolean;
    }>({ status: "loading", snapshots: {}, hasResolved: false });

    useEffect(() => {
        if (!identity) return;
        let cancelled = false;
        // Retain this account's last confirmed progress while a refresh is in
        // flight. The level UI can then show a small, local spinner instead
        // of looking like the whole header/page has been reset.
        setState((current) => current.identity === identity
            ? { ...current, status: "loading" }
            : { identity, status: "loading", snapshots: {}, hasResolved: false });
        readUserXp(identity).then((rows) => {
            if (cancelled) return;
            const snapshots: ProgressSnapshots = {};
            features.forEach((feature) => { snapshots[feature] = rows[feature]?.data; });
            setState({ identity, status: "ready", snapshots, hasResolved: true });
        }).catch(() => {
            if (!cancelled) setState((current) => current.identity === identity
                ? { ...current, status: "error" }
                : { identity, status: "error", snapshots: {}, hasResolved: false });
        });
        return () => { cancelled = true; };
    }, [identity, attempt]);

    // Never expose the previous account's data, even on the render before effect cleanup.
    const visible = identity && state.identity === identity;
    const breakdown = computeXpBreakdown(visible ? state.snapshots : {});
    return {
        breakdown, progress: xpProgress(breakdown.total),
        status: !identity ? "signed-out" : visible ? state.status : "loading",
        hasProgress: visible && state.hasResolved,
        refresh: () => {
            if (identity) invalidateUserXpRead(identity);
            setAttempt((value) => value + 1);
        },
    };
}
